import React, { useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RestoreIcon from '@mui/icons-material/Restore';

import { customerService } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import BoundedPageLayout from '../../../components/dashboard/BoundedPageLayout';
import PageHeader from '../../../components/dashboard/PageHeader';
import LoadingState from '../../../components/dashboard/LoadingState';
import ErrorState from '../../../components/dashboard/ErrorState';
import PermissionDenied from '../../../components/dashboard/PermissionDenied';
import SoftChip from '../../../components/dashboard/SoftChip';
import SuccessSnackbar from '../../../components/dashboard/SuccessSnackbar';
import { languages } from '../../../components/LanguageSelector';
import { retryOnServerError } from '../../../components/dashboard/queryOptions';
import { BusinessStatusChip, CompanyTypeChip } from '../../../components/dashboard/BusinessChips';
import { businessCapabilities, sharedNames } from './columns';
import { formatDate, formatPhone, formatPostalCode, formatTaxId } from '../../../utils/formatters';

const PRIMARY_PROPS = { variant: 'body2', color: 'text.secondary' };
const SECONDARY_PROPS = { variant: 'body1', color: 'text.primary' };

function Field({ label, value, divider = true }) {
  return (
    <ListItem disableGutters divider={divider}>
      <ListItemText
        primary={label}
        secondary={value || '—'}
        primaryTypographyProps={PRIMARY_PROPS}
        secondaryTypographyProps={typeof value === 'string' || !value ? SECONDARY_PROPS : { component: 'div' }}
      />
    </ListItem>
  );
}

const formatStreet = (address) =>
  address && [address.street, address.number, address.complement].filter(Boolean).join(', ');

const formatCity = (address) =>
  address &&
  [
    [address.city, address.state].filter(Boolean).join(' / '),
    formatPostalCode(address.postal_code, address.country),
    address.country,
  ]
    .filter(Boolean)
    .join(' · ');

export default function BusinessDetail() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { collaborator } = useAuth();
  const isInternal = Boolean(collaborator?.is_internal);
  const [flashMessage, setFlashMessage] = useState('');

  const businessQuery = useQuery({
    queryKey: ['businesses', id],
    retry: retryOnServerError,
    queryFn: () => customerService.getBusiness(id).then((res) => res.data),
  });

  const business = businessQuery.data;

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['businesses'] }),
      // Customer pages show the stores list/count.
      queryClient.invalidateQueries({ queryKey: ['customers'] }),
    ]);

  const handleDelete = async () => {
    await customerService.deleteBusiness(id);
    invalidate();
    navigate('/businesses', {
      state: { seedDelete: { ids: [id], message: t('businesses.deleteSuccess') } },
    });
  };

  const handleRestore = async () => {
    await customerService.restoreBusiness(id);
    await invalidate();
    setFlashMessage(t('businesses.restoreSuccess'));
  };

  const header = (
    <PageHeader
      title={business?.display_name ?? t('businesses.detailTitle')}
      breadcrumbs={[
        { label: t('businesses.breadcrumb.dashboard') },
        { label: t('nav.groups.settings') },
        { label: t('businesses.breadcrumb.list'), to: '/businesses' },
        { label: business?.display_name ?? t('businesses.detailTitle') },
      ]}
    />
  );

  const renderBody = () => {
    if (businessQuery.isLoading) {
      return <LoadingState message={t('businesses.states.loading')} />;
    }

    if (businessQuery.isError) {
      const status = businessQuery.error?.response?.status;
      if (status === 403) {
        return (
          <PermissionDenied
            title={t('businesses.states.permissionDeniedTitle')}
            message={t('businesses.states.permissionDeniedMessage')}
          />
        );
      }
      if (status === 404) {
        return (
          <ErrorState title={t('businesses.states.notFoundTitle')} message={t('businesses.states.notFoundMessage')} />
        );
      }
      return (
        <ErrorState
          title={t('businesses.states.errorTitle')}
          message={t('businesses.states.errorMessage')}
          actionLabel={t('businesses.states.retry')}
          onRetry={businessQuery.refetch}
        />
      );
    }

    const persona = business.persona ?? {};
    const address =
      (business.addresses ?? []).find((item) => item.address_type === 'COMMERCIAL' && item.is_primary) ?? null;
    const phones = business.phones ?? [];
    const { canEdit, canDelete, canRestore } = businessCapabilities(business);
    const hasActions = canRestore || canDelete || (canEdit && !business.is_deleted);

    return (
      <Stack spacing={3} sx={{ width: '100%', pb: 3 }}>
        <Card>
          <CardHeader
            title={t('businesses.sections.identification')}
            subheader={t('businesses.sections.identificationHint')}
            titleTypographyProps={{ variant: 'h6' }}
          />
          <Divider />
          <CardContent sx={{ py: 0 }}>
            <List disablePadding>
              <Field label={t('businesses.fields.displayName')} value={business.display_name} />
              <Field
                label={t('businesses.fields.customer')}
                value={
                  isInternal ? (
                    <Link component={RouterLink} to={`/customers/${business.customer_uuid}`} underline="hover">
                      {business.customer_name}
                    </Link>
                  ) : (
                    business.customer_name
                  )
                }
              />
              <Field
                label={t('businesses.fields.language')}
                value={
                  languages.find((option) => option.code === business.language)?.label ??
                  t('businesses.fields.languageNone')
                }
              />
              <Field
                label={t('businesses.fields.whatsapp')}
                value={
                  <SoftChip
                    size="small"
                    sx={{ mt: 0.5 }}
                    color={business.whatsapp_bot_configured ? 'success' : 'default'}
                    label={t(
                      `businesses.whatsappStatuses.${business.whatsapp_bot_configured ? 'configured' : 'notConfigured'}`,
                    )}
                  />
                }
              />
              <Field label={t('businesses.fields.createdAt')} value={formatDate(business.created_at, i18n.language)} />
              <Field
                label={t('businesses.fields.status')}
                value={<BusinessStatusChip t={t} business={business} sx={{ mt: 0.5 }} />}
                divider={false}
              />
            </List>
          </CardContent>
          {hasActions && (
            <>
              <Divider />
              <CardActions sx={{ px: 3, py: 2, gap: 1, justifyContent: 'flex-end' }}>
                {canRestore && (
                  <Button variant="contained" startIcon={<RestoreIcon />} onClick={handleRestore}>
                    {t('businesses.actions.restore')}
                  </Button>
                )}
                {canDelete && (
                  <Button variant="outlined" color="error" startIcon={<DeleteOutlineIcon />} onClick={handleDelete}>
                    {t('businesses.actions.delete')}
                  </Button>
                )}
                {canEdit && !business.is_deleted && (
                  <Button
                    variant="contained"
                    startIcon={<EditOutlinedIcon />}
                    onClick={() => navigate(`/businesses/${id}/edit`)}
                  >
                    {t('businesses.actions.edit')}
                  </Button>
                )}
              </CardActions>
            </>
          )}
        </Card>

        <Card>
          <CardHeader
            title={t('businesses.sections.company')}
            subheader={t('businesses.sections.companyHint')}
            titleTypographyProps={{ variant: 'h6' }}
          />
          <Divider />
          {business.is_shared_persona && (
            <CardContent sx={{ pb: 0 }}>
              <Alert severity="info">{t('businesses.sharedNotice', { names: sharedNames(t, business.shared_with) })}</Alert>
            </CardContent>
          )}
          <CardContent sx={{ py: 0 }}>
            <List disablePadding>
              <Field label={t('businesses.fields.fullName')} value={persona.full_name} />
              <Field label={t('businesses.fields.legalName')} value={persona.legal_name} />
              <Field label={t('businesses.fields.taxId')} value={formatTaxId(persona.tax_id, persona.country)} />
              <Field label={t('businesses.fields.registrationNumber')} value={persona.registration_number} />
              <Field label={t('businesses.fields.email')} value={persona.email} />
              <Field label={t('businesses.fields.website')} value={persona.website} />
              <Field label={t('businesses.fields.country')} value={persona.country} />
              <Field
                label={t('businesses.fields.companyType')}
                value={<CompanyTypeChip t={t} companyType={persona.company_type} sx={{ mt: 0.5 }} />}
                divider={Boolean(business.parent_company)}
              />
              {business.parent_company && (
                <Field
                  label={t('businesses.fields.parentCompany')}
                  value={business.parent_company.full_name}
                  divider={false}
                />
              )}
            </List>
          </CardContent>
        </Card>

        <Card>
          <CardHeader
            title={t('businesses.sections.address')}
            subheader={t('businesses.sections.addressHint')}
            titleTypographyProps={{ variant: 'h6' }}
          />
          <Divider />
          <CardContent sx={{ py: 0 }}>
            <List disablePadding>
              <Field label={t('businesses.fields.street')} value={formatStreet(address)} />
              <Field label={t('businesses.fields.district')} value={address?.district} />
              <Field label={t('businesses.fields.city')} value={formatCity(address)} />
              <Field
                label={t('businesses.fields.phones')}
                value={
                  phones.length > 0 && (
                    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                      {phones.map((phone) => (
                        <Stack key={phone.uuid} direction="row" spacing={1} alignItems="center">
                          <Typography variant="body1" color="text.primary">
                            {formatPhone(phone.number)}
                          </Typography>
                          <Chip size="small" label={t(`businesses.phoneTypes.${phone.type}`)} />
                        </Stack>
                      ))}
                    </Stack>
                  )
                }
                divider={false}
              />
            </List>
          </CardContent>
        </Card>
      </Stack>
    );
  };

  return (
    <BoundedPageLayout header={header}>
      {renderBody()}
      <SuccessSnackbar open={Boolean(flashMessage)} message={flashMessage} onClose={() => setFlashMessage('')} />
    </BoundedPageLayout>
  );
}
